import { DBSchema, IDBPDatabase, openDB } from 'idb';
import { PortAreaType, PortType } from 'types';

interface MyDB extends DBSchema {
    portAreas: {
        key: number;
        value: PortAreaType;
        indexes: { 'by-port': number };
    };
    portMeta: {
        key: string;
        value: PortType;
    };
}

export class PortManager {
    db: IDBPDatabase<MyDB> | null = null;

    constructor() {
        this.initDB();
    }

    async initDB() {
        if (this.db) return;

        this.db = await openDB<MyDB>('portDB', 1, {
            upgrade(db) {
                if (!db.objectStoreNames.contains('portAreas')) {
                    const store = db.createObjectStore('portAreas', { keyPath: 'id' });
                    store.createIndex('by-port', 'id');
                }
                if (!db.objectStoreNames.contains('portMeta')) {
                    db.createObjectStore('portMeta');
                }
            },
        });
    }

    // Save port metadata and port areas
    savePort = async (port: PortType): Promise<void> => {
        if (!this.db) await this.initDB();

        const { port_areas, ...portWithoutAreas } = port;

        // Save port metadata
        await this.db!.put('portMeta', portWithoutAreas, 'currentPort');

        // Save each port area individually
        if (port_areas && Array.isArray(port_areas)) {
            const tx = this.db!.transaction('portAreas', 'readwrite');

            // Clear old port areas
            const oldKeys = await tx.store.getAllKeys();
            for (const key of oldKeys) {
                await tx.store.delete(key);
            }

            for (const area of port_areas as PortAreaType[]) {
                await tx.store.put(area);
            }

            await tx.done;
        }
    };

    loadPortMeta = async (): Promise<PortType> => {
        if (!this.db) await this.initDB();
        return (await this.db?.get('portMeta', 'currentPort')) || ({} as PortType);
    };

    loadPortAreas = async (): Promise<PortAreaType[]> => {
        if (!this.db) await this.initDB();
        return (await this.db?.getAll('portAreas')) || [];
    };

    loadPort = async (): Promise<PortType> => {
        if (!this.db) await this.initDB();

        const meta = (await this.db?.get('portMeta', 'currentPort')) || ({} as PortType);
        const areas = (await this.db?.getAll('portAreas')) || [];

        // Combine metadata and areas
        return {
            ...meta,
            port_areas: areas,
        } as PortType;
    };
}
